import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { M12_ROLES, hasAnyRole } from '../../lib/roles';
import type { CertificateOfOrigin } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function CertificateDetail() {
  const { ref = '' } = useParams();
  const { persona } = useAuth();
  const isOfficer = hasAnyRole(persona?.roles, M12_ROLES.TRADE_OFFICER);
  const coQ = useApiQuery<CertificateOfOrigin>(`/api/v1/m12/certificates-of-origin/${ref}`);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  if (coQ.loading) return <Spinner label="Loading certificate…" />;
  if (coQ.error || !coQ.data) {
    return (
      <div>
        <Link to="/trade/certificates" className="text-sm font-semibold text-brand-600 hover:underline">← Back to certificates</Link>
        <div className="mt-4"><ErrorNote message={coQ.error ?? 'Certificate not found'} /></div>
      </div>
    );
  }
  const co = coQ.data;
  const canWithdraw = !['ISSUED', 'REJECTED', 'WITHDRAWN', 'REVOKED'].includes(co.state);

  const withdraw = async () => {
    setBusy('withdraw');
    setError(null);
    try {
      await api.post(
        `/api/v1/m12/certificates-of-origin/${ref}/withdraw`,
        {},
        { actingFor: co.exporterType === 'ENTITY' ? persona?.actingForBusinessId : undefined },
      );
      coQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Withdraw failed');
    } finally {
      setBusy(null);
    }
  };

  const revoke = async () => {
    setBusy('revoke');
    setError(null);
    try {
      await api.post(`/api/v1/m12/certificates-of-origin/${ref}/revoke`, { reason });
      coQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Revoke failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <Link to="/trade/certificates" className="mb-3 inline-block text-sm font-semibold text-brand-600 hover:underline">← Back to certificates</Link>
      <PageHeader title={`Certificate ${co.refNo}`} subtitle={`${co.scheme} · ${co.exporterType} ${co.exporterId}`} actions={<StatusBadge status={co.state} />} />

      {error ? <div className="mb-4"><ErrorNote message={error} /></div> : null}

      <div className="card mb-5 p-5">
        <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
          <div><dt className="text-xs uppercase text-slate-400">Cert serial</dt><dd className="font-mono">{co.certSerial ?? '— (not yet issued)'}</dd></div>
          <div><dt className="text-xs uppercase text-slate-400">Destination</dt><dd>{co.destinationCountry}</dd></div>
          <div><dt className="text-xs uppercase text-slate-400">Origin criterion</dt><dd>{co.originCriterion}{co.localValueAddedPct ? ` (${co.localValueAddedPct}% local VA)` : ''}</dd></div>
          <div><dt className="text-xs uppercase text-slate-400">Invoice</dt><dd>{co.invoiceNumber} — {formatDate(co.invoiceDate)}</dd></div>
          <div><dt className="text-xs uppercase text-slate-400">Weight</dt><dd>{co.grossWeightKg}kg gross / {co.netWeightKg}kg net · {co.packagesCount} pkgs</dd></div>
          <div><dt className="text-xs uppercase text-slate-400">Valid to</dt><dd>{formatDate(co.validTo)}</dd></div>
        </dl>
        <p className="mt-3 text-sm text-slate-700">{co.consignmentDescription}</p>
        <p className="mt-1 text-xs text-slate-400">HS codes: {co.hsCodes.join(', ')}</p>
        {co.rejectionReason ? <div className="mt-2"><ErrorNote message={co.rejectionReason} /></div> : null}
        {co.revokedReason ? <div className="mt-2"><ErrorNote message={`Revoked: ${co.revokedReason}`} /></div> : null}
      </div>

      <div className="flex flex-wrap gap-2">
        {canWithdraw ? <button className="btn-secondary" onClick={withdraw} disabled={busy !== null}>{busy === 'withdraw' ? 'Withdrawing…' : 'Withdraw'}</button> : null}
      </div>

      {isOfficer && co.state === 'ISSUED' ? (
        <div className="card mt-5 p-5">
          <h2 className="mb-2 text-sm font-bold text-slate-700">Revoke this certificate</h2>
          <div className="flex gap-2">
            <input className="field-input" placeholder="Reason…" value={reason} onChange={(e) => setReason(e.target.value)} />
            <button className="btn-danger shrink-0" onClick={revoke} disabled={busy !== null || reason.length < 3}>{busy === 'revoke' ? 'Revoking…' : 'Revoke'}</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
